import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import MediaPicker from '../../../components/admin/MediaPicker';
import { DragHandle, SortableItem, reorder } from './sortable';
import '../../../styles.css';

function InsertSectionButton({ sectionTypes, onAdd, label = 'Add section' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = sectionTypes.filter((type) => type.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="tpb-insert-zone">
      <button type="button" className="tpb-insert-btn" onClick={() => setOpen((value) => !value)}>
        <i className="fas fa-plus" aria-hidden="true" />
        {label}
      </button>
      {open && (
        <div className="tpb-insert-menu">
          <input
            className="tpb-insert-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search sections"
            aria-label="Search sections"
          />
          {filtered.map((type) => (
            <button
              key={type.value}
              type="button"
              className="tpb-insert-menu-item"
              onClick={() => {
                onAdd(type.value);
                setOpen(false);
                setQuery('');
              }}
            >
              <i className={type.icon} aria-hidden="true" />
              {type.label}
            </button>
          ))}
          {filtered.length === 0 && <p className="tpb-sidebar-hint">No matching sections.</p>}
        </div>
      )}
    </div>
  );
}

function PreviewSectionBlock({
  section,
  index,
  total,
  selected,
  meta,
  onSelect,
  onMove,
  onMoveTo,
  onRemove,
  onDuplicate,
  children,
}) {
  return (
    <SortableItem
      listId="sections"
      index={index}
      onMove={onMoveTo}
      className={`tpb-preview-block${selected ? ' is-selected' : ''}${section.hidden ? ' is-hidden' : ''}`}
    >
      <div
        className="tpb-preview-block-hit"
        onClick={(event) => {
          event.stopPropagation();
          onSelect(index);
        }}
      >
        <div className="tpb-preview-block-label">
          <DragHandle listId="sections" index={index} />
          <i className={meta.icon} aria-hidden="true" />
          <span>{meta.label}</span>
          <span className="tpb-preview-block-num">#{index + 1}</span>
          {section.hidden && <span className="admin-pill is-muted">Hidden</span>}
        </div>
        {selected && (
          <div className="tpb-preview-block-toolbar">
            <button type="button" className="admin-icon-btn" title="Move up" disabled={index === 0} onClick={(event) => { event.stopPropagation(); onMove(index, -1); }}>
              <i className="fas fa-arrow-up" aria-hidden="true" />
            </button>
            <button type="button" className="admin-icon-btn" title="Move down" disabled={index === total - 1} onClick={(event) => { event.stopPropagation(); onMove(index, 1); }}>
              <i className="fas fa-arrow-down" aria-hidden="true" />
            </button>
            <button type="button" className="admin-icon-btn" title="Duplicate" onClick={(event) => { event.stopPropagation(); onDuplicate(index); }}>
              <i className="fas fa-copy" aria-hidden="true" />
            </button>
            <button type="button" className="admin-icon-btn danger" title="Remove section" onClick={(event) => { event.stopPropagation(); onRemove(index); }}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="tpb-preview-block-content">
          {children}
        </div>
      </div>
    </SortableItem>
  );
}

function shiftSelection(selected, from, to) {
  if (typeof selected !== 'number') return selected;
  if (selected === from) return to;
  if (from < to && selected > from && selected <= to) return selected - 1;
  if (to < from && selected >= to && selected < from) return selected + 1;
  return selected;
}

export default function PageVisualEditor({
  form,
  setForm,
  editingSlug,
  message,
  onSave,
  onClose,
  onShowJson,
  sectionTypes,
  defaultSection,
  getSectionSummary,
  getSectionTypeMeta,
  SectionFields,
  renderSection,
  publicPath,
  newTitle,
  heroVariant = 'tool',
  pageExtras = null,
  topbarExtra = null,
}) {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const sections = form.content?.sections || [];
  const palette = sectionTypes.filter((type) => type.label.toLowerCase().includes(query.trim().toLowerCase()));

  const updateSection = (index, nextSection) => {
    const next = [...sections];
    next[index] = nextSection;
    setForm({ ...form, content: { sections: next } });
  };

  const addSection = (type, atIndex = sections.length) => {
    const next = [...sections];
    next.splice(atIndex, 0, defaultSection(type));
    setForm({ ...form, content: { sections: next } });
    setSelected(atIndex);
  };

  const removeSection = (index) => {
    const next = sections.filter((_, itemIndex) => itemIndex !== index);
    setForm({ ...form, content: { sections: next } });
    if (selected === index) setSelected(null);
    else if (typeof selected === 'number' && selected > index) setSelected(selected - 1);
  };

  const moveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    moveSectionTo(index, target);
  };

  const moveSectionTo = (from, to) => {
    setForm({ ...form, content: { sections: reorder(sections, from, to) } });
    setSelected((current) => shiftSelection(current, from, to));
  };

  const duplicateSection = (index) => {
    const clone = JSON.parse(JSON.stringify(sections[index]));
    const next = [...sections];
    next.splice(index + 1, 0, clone);
    setForm({ ...form, content: { sections: next } });
    setSelected(index + 1);
  };

  useEffect(() => {
    document.body.classList.add('tpb-editor-active');
    return () => document.body.classList.remove('tpb-editor-active');
  }, []);

  const selectedSection = typeof selected === 'number' ? sections[selected] : null;

  const editor = (
    <div className="tpb-shell">
      <div className="tpb-topbar" role="banner">
        <div className="tpb-topbar-left">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back
          </button>
          <div className="tpb-topbar-title">
            <strong>{editingSlug ? form.title || editingSlug : newTitle}</strong>
            {publicPath && <span>{publicPath}</span>}
          </div>
        </div>
        <div className="tpb-topbar-right">
          {message && <span className="tpb-topbar-message">{message}</span>}
          {topbarExtra}
          <label className="admin-checkbox tpb-published-toggle">
            <input type="checkbox" checked={form.published} onChange={(event) => setForm({ ...form, published: event.target.checked })} />
            Published
          </label>
          <button type="button" className="btn btn-secondary" onClick={onShowJson}>JSON</button>
          {publicPath && (
            <a href={publicPath} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> Live preview
            </a>
          )}
          <button type="button" className="btn btn-primary" onClick={onSave}>
            <i className="fas fa-save" aria-hidden="true" /> {editingSlug ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <div className="tpb-layout">
        <aside className="tpb-outline-pane">
          <div className="tpb-outline-tools">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search sections to add"
              aria-label="Search sections to add"
            />
          </div>
          <div className="tpb-section-palette">
            {palette.map((type) => (
              <button key={type.value} type="button" className="tpb-palette-btn" onClick={() => addSection(type.value, typeof selected === 'number' ? selected + 1 : sections.length)}>
                <i className={type.icon} aria-hidden="true" />
                <span>{type.label}</span>
              </button>
            ))}
            {palette.length === 0 && <p className="tpb-sidebar-hint">No matching sections.</p>}
          </div>
          <div className="tpb-section-nav">
            <div className="tpb-section-nav-head">
              <span>Page outline</span>
              <span>{sections.length} sections</span>
            </div>
            <button type="button" className={`tpb-outline-item${selected === 'hero' ? ' is-active' : ''}`} onClick={() => setSelected('hero')}>
              <i className="fas fa-image" aria-hidden="true" />
              <span>Hero banner</span>
            </button>
            {sections.map((section, index) => {
              const meta = getSectionTypeMeta(section.type);
              return (
                <SortableItem
                  key={`${section.type}-${index}`}
                  listId="sections"
                  index={index}
                  onMove={moveSectionTo}
                  className={`tpb-outline-row${section.hidden ? ' is-hidden' : ''}`}
                >
                  <DragHandle listId="sections" index={index} />
                  <button type="button" className={`tpb-outline-item${selected === index ? ' is-active' : ''}`} onClick={() => setSelected(index)}>
                    <i className={meta.icon} aria-hidden="true" />
                    <span className="tpb-outline-label">
                      <span className="tpb-outline-num">{index + 1}.</span>
                      {getSectionSummary(section)}
                    </span>
                    {section.hidden && <span className="admin-pill is-muted">Hidden</span>}
                  </button>
                </SortableItem>
              );
            })}
            {sections.length === 0 && <p className="tpb-outline-empty">No sections yet. Add one from the list above.</p>}
            <div className="tpb-outline-links">
              <button type="button" className={`tpb-outline-item${selected === 'page' ? ' is-active' : ''}`} onClick={() => setSelected('page')}>
                <i className="fas fa-cog" aria-hidden="true" />
                <span>Page settings</span>
              </button>
              <button type="button" className={`tpb-outline-item${selected === 'seo' ? ' is-active' : ''}`} onClick={() => setSelected('seo')}>
                <i className="fas fa-search" aria-hidden="true" />
                <span>SEO</span>
              </button>
            </div>
          </div>
        </aside>

        <div className="tpb-canvas-wrap">
          <div className="tpb-canvas-toolbar">
            <span><i className="fas fa-eye" aria-hidden="true" /> Live preview</span>
            <span className="tpb-canvas-hint">Click a block to edit it. Drag the handle to move it.</span>
          </div>
          <div className="tpb-canvas-scroll" onClick={() => setSelected(null)}>
            <div className="tpb-canvas-page">
              <div
                className={`tpb-preview-hero${selected === 'hero' ? ' is-selected' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelected('hero');
                }}
              >
                <div className="tpb-preview-block-label tpb-preview-block-label--hero">
                  <i className="fas fa-image" aria-hidden="true" />
                  <span>Hero</span>
                </div>
                <section className={heroVariant === 'stream' ? 'stream-hero animus-hero animus-hero-bg' : 'trophy-tracker-hero'}>
                  <div className="container">
                    <h1>
                      {form.hero.title || 'Page title'}
                      {form.hero.highlight && <span className="highlight"> {form.hero.highlight}</span>}
                    </h1>
                    {form.hero.subtitle ? <p>{form.hero.subtitle}</p> : <p className="tpb-placeholder">Hero subtitle…</p>}
                  </div>
                </section>
              </div>

              <section className={heroVariant === 'stream' ? 'stream-section page-body-section' : 'trophy-tracker-content'}>
                <div className={heroVariant === 'stream' ? 'container stream-container' : 'container'}>
                  {heroVariant === 'tool' && form.hero.image && (
                    <div className="trophy-tracker-image">
                      <img src={form.hero.image} alt={form.title || 'Preview'} />
                    </div>
                  )}
                  <InsertSectionButton sectionTypes={sectionTypes} onAdd={(type) => addSection(type, 0)} label="Insert section here" />
                  {sections.map((section, index) => (
                    <React.Fragment key={`${section.type}-${index}`}>
                      <PreviewSectionBlock
                        section={section}
                        index={index}
                        total={sections.length}
                        selected={selected === index}
                        meta={getSectionTypeMeta(section.type)}
                        onSelect={setSelected}
                        onMove={moveSection}
                        onMoveTo={moveSectionTo}
                        onRemove={removeSection}
                        onDuplicate={duplicateSection}
                      >
                        {renderSection(section)}
                      </PreviewSectionBlock>
                      <InsertSectionButton sectionTypes={sectionTypes} onAdd={(type) => addSection(type, index + 1)} />
                    </React.Fragment>
                  ))}
                  {sections.length === 0 && (
                    <div className="tpb-empty-canvas">
                      <i className="fas fa-layer-group" aria-hidden="true" />
                      <p>Your page is empty</p>
                      <span>Add a section from the left, or use the button above.</span>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>

        <aside className="tpb-sidebar tpb-inspector">
          <div className="tpb-sidebar-body">
            {selectedSection && (
              <div className="tpb-sidebar-panel admin-form">
                <div className="tpb-sidebar-panel-head">
                  <div>
                    <span className="tpb-sidebar-kicker">Section {selected + 1}</span>
                    <strong>{getSectionSummary(selectedSection)}</strong>
                  </div>
                </div>
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={!!selectedSection.hidden}
                    onChange={(event) => updateSection(selected, { ...selectedSection, hidden: event.target.checked })}
                  />
                  Hidden on the public page
                </label>
                <SectionFields section={selectedSection} onChange={(next) => updateSection(selected, next)} />
              </div>
            )}

            {selected === 'hero' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>Hero banner</h4>
                <div className="admin-grid-2">
                  <label>
                    Title before highlight
                    <input value={form.hero.title} onChange={(event) => setForm({ ...form, hero: { ...form.hero, title: event.target.value } })} />
                  </label>
                  <label>
                    Highlighted word
                    <input value={form.hero.highlight} onChange={(event) => setForm({ ...form, hero: { ...form.hero, highlight: event.target.value } })} />
                  </label>
                </div>
                <label>
                  Subtitle
                  <textarea value={form.hero.subtitle} onChange={(event) => setForm({ ...form, hero: { ...form.hero, subtitle: event.target.value } })} rows={2} />
                </label>
                <MediaPicker
                  label="Hero / preview image"
                  value={form.hero.image}
                  onChange={(url) => setForm({ ...form, hero: { ...form.hero, image: url } })}
                />
                {form.hero.image && (
                  <div className="tpb-media-preview">
                    <img src={form.hero.image} alt={form.title || 'Hero preview'} />
                  </div>
                )}
              </div>
            )}

            {selected === 'page' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>Page settings</h4>
                <label>
                  Slug (URL path)
                  <input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} disabled={!!editingSlug} placeholder="my-new-page" required />
                </label>
                <label>
                  Page title
                  <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
                </label>
                {pageExtras}
                <label>
                  Sort order
                  <input type="number" value={form.sortOrder} onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })} />
                </label>
                <p className="tpb-sidebar-hint">Drag pages in the list to change order. This number is the saved sort value.</p>
              </div>
            )}

            {selected === 'seo' && (
              <div className="tpb-sidebar-panel admin-form">
                <h4>SEO</h4>
                <label>
                  Description
                  <textarea value={form.seo.description} onChange={(event) => setForm({ ...form, seo: { ...form.seo, description: event.target.value } })} rows={3} />
                </label>
                <label>
                  Keywords
                  <input value={form.seo.keywords} onChange={(event) => setForm({ ...form, seo: { ...form.seo, keywords: event.target.value } })} />
                </label>
                <label>
                  Canonical URL
                  <input value={form.seo.url} onChange={(event) => setForm({ ...form, seo: { ...form.seo, url: event.target.value } })} />
                </label>
              </div>
            )}

            {selected === null && (
              <div className="tpb-sidebar-panel">
                <h4>Customize</h4>
                <p className="tpb-sidebar-hint">Select a section in the outline or the preview. Hero, page settings, and SEO stay in the outline while you edit.</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );

  return createPortal(editor, document.body);
}
